import { type FilterGroup, ResultsView } from '@kijiji/generated/graphql-types'
import { useSession } from 'next-auth/react'

import {
  createAutosBodyLabel,
  createAutosModelLabel,
  createAutosYearLabel,
  createCentralMakeLabel,
  createCentralModelLabel,
  createCentralYearLabel,
  createForSaleByLabel,
  createPriceLabel,
} from '@/features/advertisement/components/gpt/attributesGpt'
import { createHostnameLabel } from '@/features/advertisement/components/gpt/configGpt'
import { createDeviceLabel } from '@/features/advertisement/components/gpt/deviceGpt'
import { createPageTargetLabel } from '@/features/advertisement/components/gpt/pageGpt'
import {
  addTrackingCategories,
  createCategoryIdLabel,
  createLocationIdLabel,
  createSearchKeywordTarget,
} from '@/features/advertisement/components/gpt/searchGpt'
import { createUserLoggedInLabelKj } from '@/features/advertisement/components/gpt/userGpt'
import { PAGE_TEST_GROUP_VALUES } from '@/features/advertisement/constants/gpt'
import { type GptTargeting } from '@/features/advertisement/types/adConfig'
import {
  extractMakeAndModel,
  getFilterSelectedValues,
} from '@/features/advertisement/utils/gptTargeting'
import { removeEmptyValues } from '@/features/advertisement/utils/removeEmptyValues'
import { getGenericTargetingKeys } from '@/features/advertisement/utils/targeting/getGenericTargetingKeys'
import { getIntowowTargetingKeys } from '@/features/advertisement/utils/targeting/getIntowowTargetingKeys'
import { getUserTargetingKeys } from '@/features/advertisement/utils/targeting/getUserTargetingKeys'
import useCookiePreferences from '@/hooks/useCookiePreferences'
import { type RouteLocale } from '@/hooks/useLocale'
import { GA_PAGE_TYPE } from '@/lib/ga/constants/datalayer'
import { getSearchPageType } from '@/lib/ga/utils/getSearchPageType'
import { debug } from '@/utils/debug'

export type UseSrpGptProps = {
  categories?: string[]
  categoryIds?: string[]
  currentPage: number
  filters: FilterGroup[]
  keywords: string
  locationId: number
  routeLocale: RouteLocale
  totalListings?: number
  viewType: ResultsView
  isAutoBrp: boolean
}

// https://wiki.es.ecg.tools/pages/viewpage.action?pageId=160806983

export const useSrpGpt = ({
  categories = [],
  categoryIds,
  currentPage,
  filters,
  keywords,
  locationId,
  routeLocale,
  totalListings = 0,
  viewType,
  isAutoBrp,
}: UseSrpGptProps): GptTargeting[] => {
  const { status: authenticationStatus } = useSession()
  const { hasUserOptedOutTargeting = false } = useCookiePreferences()

  let pageType =
    totalListings > 0 ? getSearchPageType(keywords) : GA_PAGE_TYPE.ZERO_SEARCH_RESULTS_PAGE

  if (
    viewType === ResultsView.Map &&
    (pageType === GA_PAGE_TYPE.SEARCH_AD || pageType === GA_PAGE_TYPE.BROWSE_AD)
  ) {
    pageType = GA_PAGE_TYPE.MAP_SEARCH_RESULTS_PAGE
  }
  if (isAutoBrp) {
    pageType = GA_PAGE_TYPE.AUTOS_LANDING_PAGE
  }

  const generic = getGenericTargetingKeys({
    ptgValues: PAGE_TEST_GROUP_VALUES,
    pageType,
    hasUserOptedOutTargeting,
  })

  // values regarding the user's device
  const deprecatedDevice = [createDeviceLabel()]

  // values regarding the user
  const user = getUserTargetingKeys({ routeLocale, userStatus: authenticationStatus })
  const deprecatedUser = [createUserLoggedInLabelKj(authenticationStatus)]

  // values regarding the page
  const page = [createPageTargetLabel(currentPage)]

  // global values regarding the platform
  const deprecatedConfig = [createHostnameLabel()]

  // values regarding the search context
  const search = [
    createSearchKeywordTarget(keywords),
    createCategoryIdLabel(categoryIds),
    createLocationIdLabel(locationId),
    ...addTrackingCategories(categories),
  ]

  const yearFilters = getFilterSelectedValues(filters, 'year')
  const forSaleByFilters = getFilterSelectedValues(filters, 'forsaleby')
  const bodyFilters = getFilterSelectedValues(filters, 'bodytype')
  const priceFilters = getFilterSelectedValues(filters, 'priceSection')

  // carmake and carmodel filters are both nested within the 'make' filter
  const { makeFilters, modelFilters } = extractMakeAndModel(filters)

  // values regarding the attributes selected by the filters
  const attributes = [createPriceLabel(priceFilters), createForSaleByLabel(forSaleByFilters)]

  // values regarding the autos attributes selected by the filters
  const attributesAutos = [
    createAutosYearLabel(yearFilters),
    createCentralMakeLabel(makeFilters),
    createAutosBodyLabel(bodyFilters),
    createCentralYearLabel(yearFilters),
    createCentralModelLabel(modelFilters),
    createAutosModelLabel(modelFilters),
  ]

  const intowowTargeting = getIntowowTargetingKeys()

  const pageViewType = [
    {
      key: 'pageview',
      value: viewType?.toLowerCase(),
    },
  ]

  const gptKeyValues = removeEmptyValues([
    ...generic,

    // Page
    ...page,

    // Config
    ...deprecatedConfig,

    // Device info
    ...deprecatedDevice,

    // User
    ...user,
    ...deprecatedUser,

    // Search context
    ...search,

    // Attributes
    ...attributes,

    // Autos attributes
    ...attributesAutos,

    // Intowow test groups
    ...intowowTargeting,
    ...pageViewType,
  ])

  const generateGptTable = () => {
    // eslint-disable-next-line no-console
    console.table(gptKeyValues)
  }

  debug(generateGptTable)

  return gptKeyValues
}
